/**
 * CertificateRow — the certificate row, with one author.
 *
 * THE MACHINERY BEHIND THIS ROW IS NOT A ROW. Choosing a certificate starts a read; the document
 * already on file is read without being asked; a read may be held through a commit and released
 * only onto a refusal; a scan nothing can parse may be filed on an admin's own reading; and every
 * one of those states has a sentence, an id and a clearing rule. That is eight hundred lines of
 * decisions, and it stood in `areas/InsuranceArea.tsx` because there was one surface. There are two
 * now — contract setup's Insurance canvas and the insurance dossier's Policy canvas — and the third
 * copy is the one that would go wrong: `ocrOnNextFiledRead` is a MODULE-GLOBAL one-shot latch, so
 * two implementations of this row mounted over one document would spend each other's OCR pass.
 *
 * SO IT IS A HOOK THAT RETURNS PIECES, AND NOT ONE COMPONENT. The upload, the attestation block and
 * the two date fields have to be CHILDREN of `InlineFieldGroup` on the setup canvas — its caret rule
 * is `bodyRef.querySelector('[aria-invalid="true"]')` (`InlineFieldGroup.tsx:87`), which only finds a
 * refused slot inside its own body — while the footer belongs under the whole group, beside the link
 * that opens the document. One component could not be in both places, and a component per piece
 * would be four components sharing one machine through props. The host renders the pieces where its
 * own canvas needs them and owns nothing else.
 *
 * WHAT THE HOST OWNS AND WHY. The two date fields are the host's (`policy.field('startsAt')` on the
 * setup canvas, `edits` on the dossier), so the dates arrive as a controlled pair and go back through
 * `onDateChange`; the staged `File` is the host's, because on the setup canvas it is `useStagedFile`
 * on the group's own draft marker and on the dossier it is a value in `edits`; the attestation is the
 * host's for the same reason — it travels with the save. What this hook owns is everything between
 * them: the reads, the prefill, the handles, the sentences and the refusals.
 *
 * THE COMMIT EDGE ARRIVES AS A PROP, WHICH IS THE ONE THING THAT COULD NOT BE LIFTED VERBATIM. The
 * area watched `policy.saving` + `policy.state.status` + `savedAt`; a page watches its own mutation.
 * Both are the same three facts — is a write in flight, how did the last one end, and is this a
 * DIFFERENT ending from the one before it — so they arrive as `commit`, and `seq` is the third
 * because the group's `savedAt` is a minute-granularity display string (`useInlineGroup.ts:126`) and
 * two commits inside one clock minute have to be distinguishable.
 *
 * THE INVARIANTS THIS HOOK OWNS (the host owns the rest — the dirty set, the discard unit, the
 * caret's group body, refusal attribution by field name, and the two columns the write files):
 *  1. `File` identity plus the host's commit edge is the clearing mechanism. Nothing here clones,
 *     re-wraps or serialises a `File`: `certificateRead.ts:122` memoises on it and
 *     `certificatePrefill.ts:780` compares `attested.file !== staged`.
 *  2. `onFileRef` holds a ZERO-BYTE `File`, minted once per publication. A handle, not the document.
 *  3. The on-file read NEVER writes the dates. It publishes `mode: null, replaced: null`; only
 *     `settle`, `actOnPrefill` and `putBackBeforeANewRead` reach `onDateChange`.
 *  4. The staged file is in the on-file query KEY, not only in `enabled`.
 *  5. `loading` (the subject's first load) AND `refetching` (a refetch that may be repointing the
 *     row) both guard the publish effect, along with a staged file and a commit in flight.
 *  6. `published` is a TRIPLE — policy, read identity, published handle — and all three are compared.
 *  7. `attestationHolds` is asked of the DRAFT BEING SAVED (`attestationFor`), and the write re-reads
 *     the bytes as well (`data/insurance.ts:427-434`). Both, always.
 *  8. `rejection` stands down on TWO conditions: the attestation holding, and the offer being open.
 *  9. `certificateRefusal(read, true)` for a staged document; `certificateRefusal(read)` for the one
 *     already on file — only a document being CHOSEN may be told it cannot be filed.
 * 10. The attested line REPLACES the on-file warning, and takes its place in `Check again`'s
 *     `aria-describedby` rather than being appended to it.
 * 11. `attestorName === null` means "not known yet"; both sentences that print a name wait for it.
 * 12. The period banner keeps its own id (`f-insPeriod-warn`) whatever the row's `id` is, and both
 *     date fields point at it with `aria-describedby`, never `aria-errormessage`.
 * 13. `ocrOnNextFiledRead` is a module-global one-shot; exactly one row per url may be mounted.
 * 14. `decideCertificate` is re-derived at RENDER and never cached with the read, so a plate
 *     corrected on another canvas flips this row without re-downloading anything.
 * 15. `datesLocked` withholds the prefill OFFER and nothing else — the read still runs and the
 *     sentence still prints. What a host whose `onDateChange` refuses must never be shown is a
 *     link into it.
 */

import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Loader2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { Checkbox } from '@/components/ui/checkbox';
import {
    ATTEST_DATES_FIRST,
    ATTEST_OFFER,
    ATTEST_PLATE_DISAGREES,
    ATTEST_PLATE_LABEL,
    ATTEST_VEHICLE_UNRECORDED,
    attestable,
    attestationHolds,
    attestCheckboxLabel,
    attestOfferable,
    attestedFiling,
    attestedFilingLine,
    attestedPlateAgrees,
    attestedSummaryTail,
    certificateRefusal,
    decideCertificate,
    decidePrefill,
    FETCH_FAILED_NOTE,
    IDLE_PREFILL,
    prefillLine,
    SCAN_READING,
    UNNAMED_ATTESTOR,
    type Attestation,
    type CoverRead,
    type DatePair,
    type PrefillState,
} from '@/lib/certificatePrefill';
import {
    fetchAttestorName,
    ocrOnNextFiledRead,
    readFiledCertificate,
} from '@/lib/certificateOnFile';
import { readCertificateCoverPeriod } from '@/lib/certificateRead';
import type { ContractVehicle } from '@/lib/certificateVehicle';
import { businessDay, businessToday } from '@/lib/format';
import { openSignedUrl } from '@/lib/openSignedUrl';

import { Hint, LinkLike } from '../areas/blocks';
import { policyPeriodText, policyPeriodWarning } from '../data/policyDraft';
import InlineUpload from './InlineUpload';
import QuietField from './QuietField';

/**
 * The period banner's own id, which is NOT derived from the row's `id`.
 *
 * The sentence is about the two DATE FIELDS — the host's, not this row's — so it keeps the name
 * they have always pointed at. Deriving it from a row id a host may have renamed would silently
 * break an `aria-describedby` that still reads perfectly well in the markup.
 */
const PERIOD_WARNING_ID = 'f-insPeriod-warn';

/**
 * WHAT STANDS WHERE THE OFFER WOULD HAVE, ON A HOST WHOSE DATES ARE LOCKED [F7].
 *
 * It says why there is nothing to click and stops there. The door back — void and reissue — is the
 * `LockLine` above the dates and belongs to the host that knows which instrument was signed; naming
 * it twice is how two surfaces end up pointing at two different controls.
 */
const PERIOD_IS_FIXED = 'The cover period is fixed by the signed instrument.';

/**
 * `Apr 17, 2026` — ONE end of a period, in `policyPeriodText`'s own convention, for the sentence
 * that says a read cover has already ended and for the day an attested filing was made.
 *
 * Through `businessDay`, because `new Date('2026-04-17')` is UTC midnight, which west of Greenwich
 * prints April 16th — and a line whose whole job is to say WHICH day something happened on is the
 * last place that shift may land.
 */
function dayText(iso: string): string {
    const day = businessDay(iso);
    return day ? format(day, 'MMM d, yyyy') : iso;
}

/**
 * HOW THE HOST'S LAST WRITE ENDED, in the three facts this row acts on.
 *
 * `outcome` IS READ ONLY ON THE EDGE OUT OF `saving`, where it is the difference between a read
 * being applied and a read being dropped — see the release effect. `seq` is what tells two endings
 * apart: it must move on every commit that LANDS, because that is what `decidePrefill` compares to
 * notice a save that landed underneath a read still in flight.
 */
export interface CertificateCommit {
    saving: boolean;
    outcome: 'idle' | 'refused' | 'committed';
    seq: number;
}

/** The certificate already filed against this policy, and the standing of it. */
export interface CertificateOnFile {
    /** `insurances.certificate_url`. */
    url: string | null;
    /** `insurances.certificate_attested_at` — a timestamptz, or null. */
    attestedAt: string | null;
    /** `insurances.certificate_attested_by` — a `profiles.id`, or null. */
    attestedBy: number | null;
}

export interface CertificateRowProps {
    /**
     * The CARET target — the id that rides `InlineUpload`'s visible control, and the stem the
     * three footer sentences name themselves from. Attribution is by FIELD NAME, never by this.
     */
    id?: string;
    /** The cover pair as the host's inputs hold it: 'yyyy-MM-dd', or '' for empty. */
    dates: DatePair;
    onDateChange: (field: 'startsAt' | 'expiresAt', value: string) => void;
    /**
     * The host's two date fields are READS and its `onDateChange` refuses [F7] — so no door here
     * may offer to write them. Default `false`: contract setup has no freeze to tell this row
     * about and passes nothing.
     */
    datesLocked?: boolean;
    /** The staged document. The host holds it; this row decides everything about it. */
    file: File | null;
    onFileChange: (file: File | null) => void;
    /** What the admin confirmed about a document nothing here could read, or null. */
    attestation: Attestation | null;
    /** Called with `null` on every plate keystroke: any edit un-ticks the box. */
    onAttestation: (next: Attestation | null) => void;
    /** What the certificate has to name, for the contract this policy is on. */
    vehicle: ContractVehicle;
    onFile: CertificateOnFile;
    policyId: number | null;
    /** The subject read's FIRST load: no read may be published into it. */
    loading: boolean;
    /** The subject read answering AGAIN, which may be repointing this row at another policy. */
    refetching: boolean;
    disabled?: boolean;
    commit: CertificateCommit;
    /** The write's own refusal for this slot, verbatim, where the host attributed one here. */
    serverError?: string | null;
    /** For a host whose refusal list lives outside this row's render. */
    onRejection?: (refusal: string | null) => void;
    /** For a host whose collapsed head or rail lives outside this row's render. */
    onFlagChange?: (flag: string | null) => void;
}

export interface CertificateRowModel {
    /** The upload slot. Goes INSIDE the field group wherever the group owns the caret. */
    upload: ReactNode;
    /** The vehicle-unrecorded sentence and the attestation offer, in that order. */
    attestBlock: ReactNode;
    /** The period warning, which belongs under both date fields. */
    periodBanner: ReactNode;
    /** `Open the certificate`, `Check again`, and everything the filed read has to say. */
    footer: ReactNode;
    /** The stage-time refusal this row raises before anybody presses Save. */
    rejection: string | null;
    /** The attestation to send with THIS draft, or null. Asked of the draft being saved. */
    attestationFor: (draft: DatePair) => Attestation | null;
    /** An offer standing un-ticked — what a create bar names instead of the display refusal. */
    standsUnticked: boolean;
    /** The three-word tail a collapsed head or a rail row prints. */
    flag: string | null;
    /** What the two date fields put in `aria-describedby` while the period warning stands. */
    dateDescribedBy: string | undefined;
    /** Everything a discard clears here. The host clears its own draft. */
    reset: () => void;
}

export function useCertificateRow({
    id = 'f-insCert',
    dates,
    onDateChange,
    datesLocked = false,
    file,
    onFileChange,
    attestation,
    onAttestation,
    vehicle,
    onFile: filed,
    policyId,
    loading,
    refetching,
    disabled = false,
    commit,
    serverError = null,
    onRejection,
    onFlagChange,
}: CertificateRowProps): CertificateRowModel {
    const saving = commit.saving;
    const shut = disabled || loading || saving;
    const contractVehicle: ContractVehicle = { plate: vehicle.plate, vin: vehicle.vin };

    /*
     * ── THE COVER DATES, READ OFF THE CERTIFICATE ────────────────────────────
     *
     * THE DOCUMENT IS THE AUTHORITY ON ITS OWN PERIOD, so a confident read LANDS ON BOTH DATE
     * FIELDS, whatever they held. The complaint this answers is mis-ENTERED cover dates and every
     * policy in production already carries both, so a fill-only-when-empty rule would stand down
     * in exactly the case it exists for. There is no confirm and no Apply button: what was asked
     * for was an automatic prefill, and the way back — `Put back …` — is offered AFTER the write
     * rather than as a gate in front of it.
     *
     * IT ONLY MAKES THE HOST DIRTY. An apply is two ordinary edits through `onDateChange`; the
     * host's commit bar is still the only thing that writes a policy, exactly as it is for a date
     * typed by hand. Nothing here reaches the database.
     *
     * THERE ARE TWO DOORS AND ONE SET OF RULES. A certificate is read when it is STAGED, and —
     * automatically, on every policy view — when it is ALREADY ON FILE. The staged door corrects
     * the policy being recorded now; the second one is how the certificates ALREADY in storage get
     * checked, which is the whole of the census that came with this feature: it read 39 filed
     * certificates, 38 of them on active contracts, and found seven contradicting their own stored
     * period. It could otherwise only be answered by re-uploading a document that is already there.
     *
     * THE TWO DOORS ARE NO LONGER ONE MACHINE, AND THE SPLIT IS WHERE THE DATES ARE. `decidePrefill`
     * — the rules about overwriting, holding through a commit and dropping a stale read — governs
     * the STAGED door alone, because only a staged read applies anything. The on-file read shares
     * what it still needs: the `File`-identity subject in `onFileRef`, so Discard, a staged
     * document, a repointed row and a landed commit all retire its line at no cost, and
     * `actOnPrefill`, so its `Use the certificate’s dates` is the same click the staged door makes.
     *
     * A READ IS HELD WHILE THE HOST IS SAVING, AND RELEASED ONLY ONTO A REFUSAL. An apply
     * mid-commit has nowhere to go: the inline reducer refuses an `edit` under `saving` (rule 7),
     * and if it did not, the block would leave `saving` and offer Save again over a write that
     * CREATES rows. So the read waits, and the release effect below decides on the `saving → false`
     * edge which of the two exits it was:
     *
     *  - REFUSED — the Save-pressed-on-empty-dates case the hold exists for. Nothing committed, the
     *    certificate is still staged, and the dates the admin is being asked for are sitting in the
     *    document: the read applies.
     *  - COMMITTED (or discarded) — the certificate was filed with the dates as they were typed, so
     *    the read is dropped where it stands. THIS CANNOT BE LEFT TO `decidePrefill`: the staged
     *    file is nulled by the host in a LATER effect, so `live.current.staged` is still the same
     *    `File` on this pass, and its staleness rules would miss.
     *
     * AND THE LINE IS KEYED TO `File` IDENTITY, WHICH IS THE WHOLE CLEARING RULE. Every pick makes
     * exactly one File object, so Remove, Replace, Discard and a landed commit — all of which null
     * the staged file at the host — take the sentence with them at no cost. The line can never
     * outlive the document it describes, and the worst a refactor can do is stop drawing it.
     *
     * `live` IS ASSIGNED AT RENDER AND READ ONLY FROM THE ASYNC CONTINUATIONS, never closed over. A
     * read resolves a second or two after it starts, by which time a captured `saving` is a
     * statement about a host that has since committed, been discarded, or moved to another policy.
     */
    const [prefill, setPrefill] = useState<PrefillState>(IDLE_PREFILL);
    /*
     * ── AND WHAT THE ADMIN SAYS ABOUT A SCAN NOTHING HERE CAN READ [D-B56] ───
     *
     * TWO PIECES OF STATE AND NOT ONE, because the plate is typed for as long as it takes to get
     * right and the confirmation is an instant. `attestPlate` is the input's own value; the
     * `attestation` prop is the whole claim as it stood when the box was ticked — the document, the
     * plate, both dates — and `attestationHolds` re-asks every render whether that claim is still
     * true of what is on the screen now. So a date typed over un-ticks the box without anything
     * having to notice, and a plate edited after the tick drops the claim on the keystroke, which
     * is the one part of "any edit un-ticks" the pure rule cannot see for itself.
     *
     * THE PLATE IS THIS ROW'S AND THE CLAIM IS THE HOST'S, because the claim travels with the save
     * and the plate never leaves this block.
     */
    const [attestPlate, setAttestPlate] = useState('');
    const pending = useRef<{
        file: File;
        stamp: string;
        result: { startsOn: string; expiresOn: string } | null;
    } | null>(null);
    /**
     * THE ON-FILE READ'S OWN SLOT, WHICH IS WHAT THE STAGED FILE IS FOR A PICKED DOCUMENT.
     *
     * IT HOLDS A ZERO-BYTE `File` AND NEVER THE DOCUMENT ITSELF. The bytes live in the query's
     * cache; what this slot is for is giving the on-file line a SUBJECT with an identity, so the
     * whole read is keyed by `File` identity like every other and the line clears itself on a
     * staged file, on a discard, on a repointed row and on a landed commit at no cost. One handle
     * is minted per publication (see the publish effect), so a re-check is a different subject from
     * the one before it.
     *
     * A REF AND NOT STATE, because the staged read's continuations read it and a captured copy
     * would be a statement about a slot that has since been superseded.
     *
     * READING IT DURING RENDER IS HONEST BECAUSE THE NOTE NEEDS BOTH HALVES TO AGREE, not because
     * every write is paired with a `setPrefill` — `reset()` nulls it without touching prefill at
     * all. The line is drawn only where this slot is non-null AND `prefill.file` is the very same
     * object, so a slot cleared on its own stops drawing the sentence rather than leaving a stale
     * one, and every write here happens inside a handler or an effect that a render follows.
     */
    const onFileRef = useRef<File | null>(null);
    /**
     * THE UNDO, READ FROM THE PUBLISH EFFECT RATHER THAN CLOSED OVER — the `live` idiom, one
     * function wide.
     *
     * `putBackBeforeANewRead` is rebuilt every render over `prefill`, the dates and the two slots,
     * and the publish effect needs the CURRENT one: a copy captured when that effect's deps last
     * changed would be deciding on a draft the admin has since typed into.
     */
    const putBackRef = useRef<() => void>(() => {});
    /**
     * HOW MANY COMMITS HAVE LANDED, WHICH IS THE ONLY PART OF `seq` A READ MAY COMPARE AGAINST.
     *
     * `decidePrefill` asks whether a commit landed underneath a read that was already in flight,
     * and answers by comparing this stamp at the start of the read against this stamp now. A
     * REFUSAL must not move it: the refusal is exactly the ending on which the held read is
     * released and applied, and a stamp that moved there would have `decidePrefill` drop the read
     * the release effect just went to the trouble of handing it. So a host may bump `seq` on every
     * ending it likes; only a `committed` one is recorded here.
     *
     * ASSIGNED AT RENDER, like `live` below it, so an async continuation reads the stamp as it
     * stands when the read resolves rather than as it stood when the read began.
     */
    const landed = useRef(0);
    if (commit.outcome === 'committed') landed.current = commit.seq;
    const live = useRef({
        staged: file,
        saving,
        /** A string because that is what `decidePrefill` compares; only inequality is read. */
        stamp: String(landed.current),
        dates,
        /** WHAT THE CERTIFICATE HAS TO NAME, read when the parse resolves rather than closed over. */
        vehicle: contractVehicle,
    });
    live.current = {
        staged: file,
        saving,
        stamp: String(landed.current),
        dates,
        vehicle: contractVehicle,
    };

    /** The one place a read is ever applied: `decidePrefill` rules, this dispatches what it says. */
    const settle = useCallback(() => {
        const held = pending.current;
        const result = held?.result;
        if (!held || !result) return;

        const now = live.current;
        const decision = decidePrefill({
            readFor: held.file,
            // ONLY A STAGED READ EVER REACHES HERE. The certificate already on file is read
            // automatically and NEVER APPLIES ITS DATES (see the on-file query below), so the door
            // that used to hand this its subject out of `onFileRef` is gone with it.
            staged: now.staged,
            savedAtAtStart: held.stamp,
            savedAtNow: now.stamp,
            saving: now.saving,
            current: { startsAt: now.dates.startsAt, expiresAt: now.dates.expiresAt },
            read: result,
        });

        // A held read stays pending — the release effect below answers it. THE LINE IS UPDATED
        // NOW RATHER THAN THEN: the read HAS resolved, and a note that went on saying `Reading the
        // certificate…` for the length of a save would be describing work that finished. `mode`
        // and `replaced` stay null because nothing has been written yet, and the action the line
        // would carry is not drawn while the host is saving.
        if (decision.kind === 'hold') {
            setPrefill((state) =>
                state.file === held.file && state.read?.status === 'reading'
                    ? { ...state, read: { status: 'found', ...result } }
                    : state,
            );
            return;
        }
        pending.current = null;
        if (decision.kind === 'drop') return;

        if (decision.set.startsAt !== undefined) onDateChange('startsAt', decision.set.startsAt);
        if (decision.set.expiresAt !== undefined) onDateChange('expiresAt', decision.set.expiresAt);
        setPrefill({
            file: held.file,
            read: { status: 'found', ...result },
            mode: decision.mode,
            replaced: decision.replaced,
        });
    }, [onDateChange]);

    /*
     * THE RELEASE, AND IT IS A TRANSITION GATE RATHER THAN A LEVEL CHECK. It fires once, on the
     * edge out of `saving`, and only a write that ended in a REFUSAL gets the held read applied —
     * see the header for why a landed commit cannot be left to `decidePrefill` to notice. A
     * committed or discarded host drops the read here instead, which is the same thing
     * `decidePrefill` would have done had it been able to see the ending.
     */
    const wasSaving = useRef(false);
    useEffect(() => {
        if (commit.saving) {
            wasSaving.current = true;
            return;
        }
        if (!wasSaving.current) return;
        wasSaving.current = false;
        if (commit.outcome === 'refused') {
            settle();
            return;
        }
        pending.current = null;
        // AND A LANDED COMMIT RETIRES THE ON-FILE READ-OUT TOO. A staged read's line goes with the
        // staged file (the host nulls it on a commit); nothing nulls this slot, so without this the
        // sentence would outlive the save and go on offering `Put back …` — which would re-dirty a
        // just-saved policy with the values the certificate had just corrected.
        const subject = onFileRef.current;
        if (subject === null) return;
        onFileRef.current = null;
        setPrefill((state) => (state.file === subject ? IDLE_PREFILL : state));
    }, [commit.saving, commit.outcome, settle]);

    /**
     * THE WAY BACK LEAVES WITH THE READ THAT JUSTIFIED THE OVERWRITE.
     *
     * `Put back …` is drawn under this row and keyed to the document the standing prefill speaks
     * for, so anything that retires that document — Replace, Remove, or a fresh read of the
     * certificate on file — would take the only route back to the dates the read wrote over,
     * leaving a period nobody typed on a document that is no longer the subject. If the fields
     * still read exactly what the departing read said, the replaced pair goes back FIRST; the new
     * read lands over it a moment later and captures the restored values as its own `replaced`,
     * which is the same offer made about the document actually being read.
     *
     * GATED ON THE SAME IDENTITY THE LINES ARE. A certificate that has been COMMITTED is no longer
     * the subject of anything — the host nulls a staged file on a landed commit and the release
     * effect nulls `onFileRef` — its dates are the server's own, and the offer to put anything back
     * went with it: restoring then would re-dirty a saved policy with the values the filed document
     * corrected.
     *
     * THREE DOORS CALL IT AND THAT IS THE WHOLE LIST: `stageCertificate` (a document picked,
     * replaced or removed), `Check again` (a second read of the same filed document, started by
     * hand), and the publish effect (a read that landed on its own and is about to replace the
     * standing one). The last two are what make the on-file door honest — without them a re-check
     * silently kept the dates the first read wrote and took the way back away with it.
     */
    function putBackBeforeANewRead() {
        const subject = prefill.file;
        const back = prefill.replaced;
        const read = prefill.read;
        if (
            subject === null ||
            (subject !== file && subject !== onFileRef.current) ||
            prefill.mode !== 'replaced' ||
            back === null ||
            read === null ||
            read.status !== 'found' ||
            dates.startsAt !== read.startsOn ||
            dates.expiresAt !== read.expiresOn
        ) {
            return;
        }
        onDateChange('startsAt', back.startsAt);
        onDateChange('expiresAt', back.expiresAt);
    }
    // ASSIGNED AT RENDER, READ ONLY FROM THE PUBLISH EFFECT — the `live` idiom, one function wide.
    putBackRef.current = putBackBeforeANewRead;

    /**
     * The parse leg, which is the same work whichever door the document came through: announce the
     * read, register it as the one pending, and answer it against the rules when it resolves.
     *
     * THE STAMP IS CAPTURED HERE, at the moment the parse starts, because that is what
     * `decidePrefill` compares against to notice a commit that landed underneath it.
     */
    function startCoverRead(file: File) {
        setPrefill({ file, read: { status: 'reading' }, mode: null, replaced: null });
        pending.current = { file, stamp: live.current.stamp, result: null };
        void readCertificateCoverPeriod(file, {
            /*
             * THE WAIT LEARNS WHAT IT IS WAITING FOR. A certificate with a text layer is read
             * between two frames; a scan is rasterised at 300 dpi and put through an OCR pass, and
             * from this row that looks exactly like a read that has hung. The reader says which
             * branch it took and the note names it.
             *
             * GUARDED THE SAME WAY THE RESOLUTION BELOW IS, on the file this read speaks for: a
             * phase landing after a newer pick would rewrite that document's own line.
             */
            onPhase: () => {
                if (pending.current?.file !== file) return;
                setPrefill((state) =>
                    state.file === file
                        ? { ...state, read: { status: 'reading', phase: 'ocr' } }
                        : state,
                );
            },
        })
            // THE READER OWNS ITS OWN FAILURES AND THIS IS THE BACKSTOP FOR THE ONES IT DOES NOT.
            // A throw escaping here would leave `pending` set and the note spinning on `Reading the
            // certificate…` for as long as the file stays staged, with no way back but a re-pick.
            .catch(() => ({ status: 'unreadable', reason: 'failed' }) as const)
            .then((read) => {
                // A newer pick, a Remove or a discard got here first: this read speaks for a
                // document that is no longer in the slot, and the only honest thing to do with it
                // is nothing.
                const held = pending.current;
                if (!held || held.file !== file) return;

                /*
                 * IS THIS A CERTIFICATE, AND IS IT THIS CAR'S? Asked before anything is done with
                 * its dates, because a document that cannot be verified has no dates worth having:
                 * a rejected read falls through to the branch below, which publishes the sentence
                 * and leaves the two date fields exactly as the admin left them.
                 */
                const outcome = decideCertificate(read, live.current.vehicle);

                if (outcome.status === 'found') {
                    held.result = { startsOn: outcome.startsOn, expiresOn: outcome.expiresOn };
                    settle();
                    return;
                }

                pending.current = null;
                setPrefill((state) => (state.file === file ? { ...state, read: outcome } : state));
            });
    }

    /** Staging is where a read starts — and where every earlier one stops mattering. */
    function stageCertificate(next: File | null) {
        putBackBeforeANewRead();

        onFileChange(next);
        pending.current = null;
        // AND THE ADMIN'S WORD LEAVES WITH THE DOCUMENT IT WAS GIVEN ABOUT. `attestationHolds`
        // would refuse the stale claim on its own — the `File` identity it carries is gone — but
        // the typed plate is the admin's work on the row, and leaving it standing under a document
        // nobody has looked at yet is an invitation to tick a box about the wrong page.
        setAttestPlate('');
        onAttestation(null);
        // A DOCUMENT IN THE SLOT ENDS ANY READ OF THE ONE ON FILE, INCLUDING A REMOVE. The row is
        // about to describe the staged file (or nothing), the link that starts an on-file read is
        // not drawn while a file is staged, and a read still in flight for the filed certificate
        // would otherwise land its dates on a block that has moved on.
        onFileRef.current = null;
        if (!next) {
            setPrefill(IDLE_PREFILL);
            return;
        }

        // Settled here rather than left to the reader, so the slot never says it is reading a file
        // it refuses a tick later. `readCertificateCoverPeriod` makes the same check again anyway.
        // IT GOES THROUGH `decideCertificate` LIKE EVERY OTHER READ, because since the 2026-09-12
        // ruling an unreadable document is a REFUSAL — which is how an image staged into this row
        // gets its sentence, and how the row turns `.sd-up-errored` over it.
        const isPdf = next.type === 'application/pdf' || /\.pdf$/i.test(next.name);
        if (!isPdf) {
            setPrefill({
                file: next,
                read: decideCertificate(
                    { status: 'unreadable', reason: 'not-pdf' },
                    live.current.vehicle,
                ),
                mode: null,
                replaced: null,
            });
            return;
        }

        startCoverRead(next);
    }

    /*
     * ── THE CERTIFICATE ALREADY ON FILE, CHECKED WITHOUT BEING ASKED ─────────
     *
     * THE CLICK IS GONE AND THE CHECK IS AUTOMATIC (owner's ruling, 2026-09-12: "Already uploaded
     * certs should show a warning"). `Read the dates from the certificate on file` was a link an
     * admin had to know to press, on a question they had no reason to think needed asking — and
     * the census behind it found SEVEN of 39 filed certificates contradicting their own stored
     * period. A warning nobody asks for is the only kind that reaches those rows.
     *
     * IT WARNS AND IT NEVER REFUSES, WHICH IS THE ONLY DIFFERENCE FROM THE STAGED DOOR. A document
     * being chosen is stopped before it reaches storage; one already IN storage cannot be un-filed
     * by a sentence, and telling an admin that a certificate they are looking at "cannot be filed"
     * when it demonstrably was is how a warning teaches people to ignore warnings. So the same
     * `certificateRefusal` writes it, without `cannotFile`, and nothing is blocked by it.
     *
     * AND IT NEVER APPLIES ITS DATES, WHICH IS WHAT MAKES READING ON LOAD SAFE. The objection this
     * feature shipped with — a read that fires on load would overwrite two dates in a block the
     * admin did not come to edit — is answered by the read having no route to the fields at all:
     * it lands as a prefill with `mode: null` and `replaced: null`, so the line under the hint is a
     * STANDING FACT (`The certificate reads …`) carrying the one click that applies it. Nothing is
     * written until the host's commit bar is pressed, exactly as before.
     *
     * ONE SIGNED URL AND ONE DOWNLOAD PER POLICY VIEW. The entry is keyed on the stored URL with
     * `staleTime: Infinity`, so the bytes come down once however many times this row re-renders,
     * and every surface mounting this hook shares the very same entry. `Check again` is the way to
     * spend another one.
     *
     * THE VERDICT IS DERIVED AT RENDER AND NOT CACHED WITH THE READ. `decideCertificate` compares
     * the document against the VEHICLE, which is not part of the key and is corrected elsewhere —
     * so re-deriving is what lets a plate typed there change this sentence, and the collapsed
     * summary's tail with it, without re-downloading anything.
     *
     * A STAGED FILE IS FOLDED INTO THE KEY AND NOT ONLY INTO `enabled`. The row is about THAT
     * document while one is staged — it has its own read, its own line and its own refusal, and two
     * certificates answering one row is two answers to one question — so the key moves to `null`
     * and every derived flag moves with it: `isFetching` goes false, `isError` goes false, and the
     * two lines below need no second guard of their own to stop contradicting the staged read.
     * `enabled` alone left a spent `isError` standing under a document nobody had failed to fetch.
     * The URL's own entry is untouched in cache, so Removing the staged file restores the standing
     * fact without spending a signed URL.
     */
    const onFileUrl = file === null ? filed.url : null;
    /**
     * THE FILED WAIT LEARNS WHAT IT IS WAITING FOR, EXACTLY AS THE STAGED ONE DOES. `Check again`
     * is the only door that spends an OCR pass on a document already in storage, so it is the
     * slowest wait this row has — and the line it sits under says nothing but 'Checking the
     * certificate on file…', which over fifteen seconds is indistinguishable from a row that hung.
     * The reader says which branch it took; this remembers it for as long as the fetch is running,
     * and `isFetching` going false is what puts it back.
     */
    const [onFileScanning, setOnFileScanning] = useState(false);
    const onFile = useQuery({
        queryKey: ['certificate-on-file', onFileUrl],
        queryFn: () => readFiledCertificate(onFileUrl as string, () => setOnFileScanning(true)),
        enabled: onFileUrl !== null,
        staleTime: Infinity,
        gcTime: 60 * 60 * 1000,
        retry: 1,
    });
    useEffect(() => {
        if (!onFile.isFetching) setOnFileScanning(false);
    }, [onFile.isFetching]);
    /**
     * MEMOISED ON THE READ AND THE TWO IDENTIFIERS, AND THE IDENTITY IS LOAD-BEARING. The publish
     * effect below compares `published.current.read` against this by OBJECT IDENTITY to decide
     * whether the standing publication is still the current one — so a verdict rebuilt on every
     * render would publish on every render, and each publication sets state, which renders again.
     * The deps are the scalars rather than the vehicle object, so a host that rebuilds that object
     * every render costs nothing.
     */
    const onFileData = onFile.data;
    const onFileDecision = useMemo(
        () =>
            onFileData === undefined
                ? null
                : decideCertificate(onFileData, { plate: vehicle.plate, vin: vehicle.vin }),
        [onFileData, vehicle.plate, vehicle.vin],
    );

    /**
     * THE CERTIFICATE ON FILE STANDS ON SOMEBODY'S READING [D-B56] — or it does not, and this is
     * the whole of the difference.
     *
     * WITHOUT THIS THE OFFER ABOVE WOULD BE A TRAP. The filed scan is re-read on every policy view
     * and fails again every time, so a document an admin was authorised to file would wear the
     * refusal banner and the `certificate not verified` summary tail for the rest of its life. The
     * two columns are what the banner reads instead.
     *
     * BOTH HALVES ARE REQUIRED AND THE READ IS THE SECOND OF THEM. `attestedFiling` asks whether
     * the document is still one nothing here can read: a readable certificate filed over the scan
     * clears the columns as it lands, and a row that somehow carries both is believed about the
     * PAPER rather than about the record.
     */
    const attestedOnFile = attestedFiling(onFileDecision, {
        attestedAt: filed.attestedAt,
        attestedBy: filed.attestedBy,
    });
    /**
     * WHOSE READING IT WAS. `certificate_attested_by` is a `profiles` id and the sentence names a
     * person, so the id is resolved through its own entry — keyed on the id, cached for the
     * session, and never asked at all on the overwhelming majority of policies, which carry no
     * attestation. A profile that cannot be named answers `UNNAMED_ATTESTOR`: the filing happened
     * whether or not its author is still on the books, and `ON DELETE SET NULL` on the column says
     * the schema agrees.
     *
     * BUT NOT WHILE THE QUERY IS STILL IN THE AIR. `an admin` is the answer for a profile that has
     * GONE; printed during the round-trip it asserts that this filing has no nameable author and
     * then corrects itself in front of the reader on the next frame. `null` is the third state —
     * not known yet — and the two sentences that print a name wait for it.
     */
    const attestorId = attestedOnFile?.attestedBy ?? null;
    const attestor = useQuery({
        queryKey: ['certificate-attestor', attestorId],
        queryFn: () => fetchAttestorName(attestorId),
        enabled: attestorId !== null,
        staleTime: 60 * 60 * 1000,
    });
    const attestorName =
        attestorId !== null && attestor.isPending ? null : (attestor.data ?? UNNAMED_ATTESTOR);

    /**
     * THE READ THAT PASSED, HANDED TO THE PREFILL MACHINERY AS AN ON-FILE SUBJECT.
     *
     * A REJECTED READ NEVER COMES THROUGH HERE. Its sentence is a warning under the hint, printed
     * straight off `onFileDecision`, and it has nothing to say about the two date fields — there is
     * no period on this row worth offering from a document nobody could verify.
     *
     * THE ZERO-BYTE `File` IS THE SUBJECT. Every clearing rule here is `File` identity — a discard,
     * a repointed row, a staged document, a landed commit — so publishing through one buys all of
     * them unchanged, including `Put back …` leaving with the read that justified it.
     *
     * IT REPUBLISHES AFTER A COMMIT AND NOT BEFORE ONE, and the guard is the TRIPLE rather than the
     * read: `published.current.read` is the object identity of the decision it spoke for,
     * `published.current.policyId` is the policy it spoke for, and `prefill.file` says whether that
     * publication is still standing. A landed commit retires the slot (see the release effect) and
     * this puts the standing fact back on the next pass — with `mode: null`, so what comes back is
     * the sentence and never the `Put back …` that would re-dirty a just-saved policy. An admin who
     * has clicked `Use the certificate’s dates` keeps their state: the prefill still belongs to the
     * published handle, so nothing fires.
     *
     * ── AND IT MUST NOT RUN WHILE THE SUBJECT READ IS REFETCHING, WHICH IS WHY `refetching` IS A
     * GUARD AND `loading` WAS NOT ENOUGH.
     *
     * `loading` is the FIRST load only. A link, swap, unlink or delete invalidates the subject read,
     * and react-query serves the entry it already has while it refetches — so for the length of
     * that round trip the policy is still the DEPARTED one, its `certificate_url` is still the
     * departed document, and the `certificate-on-file` entry keyed on that URL is still resolved.
     * A host that has just called `reset()` in the act's `onSuccess` leaves the guard below seeing
     * `standing === null`, believing nothing is published, and re-publishing the departed
     * certificate's read onto a row that is already about another policy — or about no policy at
     * all after an unlink. The sentence then names a document this contract is no longer on, and
     * its `Use the certificate’s dates` writes that document's period into the new policy's fields.
     *
     * `refetching` closes exactly that window: it is true from the invalidation until the read
     * answers, and by the time it falls the URL, the decision and the policy id are the new
     * policy's. `policyId` on the handle is the second half of the same rule — a publication is
     * pinned to the policy it was made for, so one made for the departing policy can never be
     * mistaken for still standing over the arriving one, whatever the read identity says.
     */
    const published = useRef<{ policyId: number | null; read: CoverRead; file: File } | null>(null);
    const prefillFile = prefill.file;
    useEffect(() => {
        const decision = onFileDecision;
        const standing = published.current;

        /*
         * A REJECTED READ RETIRES WHATEVER WAS STANDING, AND THE CASE THAT NEEDS IT IS THE VEHICLE
         * BEING CORRECTED UNDERNEATH: the plate is typed on the Vehicle block, the verdict flips
         * from `found` to `mismatch`, and without this the standing-fact line would go on saying
         * what the certificate reads one row above a warning saying it is not this car's paper.
         * The dates are left where they are — an admin who applied them did so deliberately.
         */
        if (decision === null || decision.status !== 'found') {
            if (standing === null) return;
            published.current = null;
            if (onFileRef.current === standing.file) onFileRef.current = null;
            setPrefill((state) => (state.file === standing.file ? IDLE_PREFILL : state));
            return;
        }

        // The three states no read may be published in — a host mid-commit, a subject read that has
        // not answered, and one answering AGAIN over an act that just changed which policy this row
        // is about — and the one where the row is about another document, which `stageCertificate`
        // has already cleared the slot for.
        if (file || saving || loading || refetching) return;
        if (
            standing?.read === decision &&
            standing.policyId === policyId &&
            prefillFile === standing.file
        ) {
            return;
        }

        // THE WAY BACK LEAVES WITH THE READ THAT JUSTIFIED IT, AND THIS IS THE SECOND DOOR INTO
        // THAT RULE — `stageCertificate` is the first. A standing on-file prefill the admin applied
        // carries `Put back …`, and the publication about to replace it would take that offer away
        // while leaving the dates it wrote: a period nobody typed, on a document that is no longer
        // the subject. `putBackBeforeANewRead` is a no-op unless the fields still read exactly what
        // the departing read said, so it cannot undo an edit made since.
        putBackRef.current();

        const handle = new File([], 'certificate-on-file');
        published.current = { policyId, read: decision, file: handle };
        onFileRef.current = handle;
        // A STAGED READ IN FLIGHT WOULD BE ORPHANED, so it is dropped rather than left to land on a
        // row whose line now belongs to the filed document. It can only exist here if the file it
        // was started for has already gone (`stageCertificate` nulls `pending` on every pick), so
        // this is the same clearing `reset()` does and not a race with a live read.
        pending.current = null;
        setPrefill({ file: handle, read: decision, mode: null, replaced: null });
    }, [onFileDecision, file, saving, loading, refetching, prefillFile, policyId]);

    /**
     * The single control, whose label flips with what the fields say.
     *
     * `restore` puts back exactly the pair that stood the instant before the apply, AS A PAIR — an
     * overwritten empty half goes back to empty, because undoing half of an overwrite would leave a
     * period that was never on any document. `reapply` writes the read again over whatever has been
     * typed since. Neither is reachable while the host is saving: the action is not drawn then.
     */
    function actOnPrefill(kind: 'restore' | 'reapply') {
        const read = prefill.read;
        if (read === null || read.status !== 'found') return;

        if (kind === 'restore') {
            const back = prefill.replaced;
            if (back === null) return;
            onDateChange('startsAt', back.startsAt);
            onDateChange('expiresAt', back.expiresAt);
            // `mode` stands: the draft now differs from the read, so the line stops claiming a fill
            // and becomes the standing fact with the inverse action.
            setPrefill((state) => ({ ...state, replaced: null }));
            return;
        }

        const before = { startsAt: dates.startsAt, expiresAt: dates.expiresAt };
        onDateChange('startsAt', read.startsOn);
        onDateChange('expiresAt', read.expiresOn);
        setPrefill((state) => ({ ...state, mode: 'replaced', replaced: before }));
    }

    /**
     * EVERYTHING A DISCARD CLEARS HERE, AND THE HOST CLEARS ITS OWN DRAFT.
     *
     * Every caller changes WHAT THIS ROW HOLDS — a discard puts the fields back to the server's
     * values, and the acts that repoint a row change which policy it is about — and a read started
     * before that would otherwise land on top of the result (the host clears the staged file in a
     * later effect, so `decidePrefill`'s staleness check can still see the departing `File` on the
     * pass that follows).
     *
     * AND THE PREFILL GOES WITH THE SLOT, WHICH IS WHAT LETS THE LINE COME BACK. The on-file read
     * is automatic, so nulling the slot alone would leave the published prefill pointing at a
     * handle nothing names — a line that has silently stopped drawing and that no re-render can
     * restore. Retiring both is what the publish effect watches for: it sees an idle prefill
     * against a standing decision and republishes the standing fact, with `mode: null`, on the
     * policy this row is now about.
     */
    const reset = useCallback(() => {
        // THE ATTESTATION IS PART OF THE DRAFT IN EVERYTHING BUT WHERE IT IS STORED. A confirmation
        // given over one period, or over another policy's document, is not a confirmation about
        // what is left.
        setAttestPlate('');
        onAttestation(null);
        pending.current = null;
        const subject = onFileRef.current;
        onFileRef.current = null;
        published.current = null;
        if (subject !== null) {
            setPrefill((state) => (state.file === subject ? IDLE_PREFILL : state));
        }
    }, [onAttestation]);

    // The banner is the draft's, so a prefilled non-annual period and a hand-typed one warn
    // identically, and a half-filled block is never nagged (the helper is null unless both ends
    // read). It is advisory to the last: `validatePolicyDraft` never sees it and Save never waits.
    const periodWarning = policyPeriodWarning(dates.startsAt, dates.expiresAt);

    // THE DRAFT IS RE-READ HERE EVERY RENDER, WHICH IS WHAT KEEPS THE LINE HONEST: the instant a
    // prefilled date is typed over, it stops claiming a fill and becomes the standing fact.
    const prefillSentence = prefillLine(
        prefill,
        dates,
        (a, b) => policyPeriodText(a, b) ?? '',
        // Today on the Ghana calendar, so the ended-cover sentence flips at Accra midnight rather
        // than the viewer's. The module holds no clock of its own — see `prefillLine`.
        format(businessToday(), 'yyyy-MM-dd'),
        dayText,
    );
    const offeredAction = prefillSentence?.tone === 'read' ? prefillSentence.action : null;

    /**
     * AND A LOCKED PERIOD IS OFFERED NOTHING TO DO TO IT [F7].
     *
     * `datesLocked` is a host whose two date fields are reads — the policy dossier's, on a year
     * whose instrument is signed. The read still runs and the sentence still prints, because what
     * the certificate says about the cover is worth knowing either way; what cannot stand is the
     * LINK beside it. `Use the certificate's dates` and `Put back …` both call `onDateChange`, and
     * that host refuses the call, so the offer is one the app has no intention of honouring: a
     * click that changes nothing, on the one screen whose whole subject is a signed document.
     *
     * THE LINE REPLACES THE LINK RATHER THAN BEING ADDED TO THE SENTENCE. It stands exactly where
     * the offer would have, and only where there was one — an agreeing read says nothing new, and
     * a row printing "the period is fixed" under every certificate would be nagging about a rule
     * the `LockLine` above the dates has already stated.
     */
    const periodIsFixed = datesLocked && offeredAction !== null;
    const prefillAction = datesLocked ? null : offeredAction;

    /**
     * THE FALLBACK IS OFFERED ON THE UNREAD ENDINGS AND GUARDED ON THE SAME IDENTITY EVERYTHING
     * ELSE ON THIS ROW IS [D-B56]. `attestOfferable` admits only the endings that mean nothing read
     * this document at all — never the ones that mean it was read and judged — so a loan agreement
     * filed as a certificate, an image, a car that is not this one and a text layer the parser
     * could not pin a period in all keep their refusal and their silence, and the scan gets a door.
     *
     * AND SINCE THE SCAN BRANCH, "READ IT" TURNS ON THE READER TOO. A gate refusing OCR output is
     * a gate refusing this app's guess at some pixels, so a scan the pass could not get an Act
     * title or a plate out of keeps the door; the identical verdict over the insurer's own text
     * layer does not. The whole grid is `attestable`'s, pinned cell by cell in its table test —
     * this row decides nothing about which endings qualify.
     *
     * AND NEVER ON A VEHICLE THE APP HOLDS NO PLATE FOR, because the confirmation is an agreement
     * between the document and the record and there is no record to agree with. What is drawn
     * there instead names the vehicle block, which is the thing that can actually be fixed.
     */
    const attestStaged = file !== null && prefill.file === file;
    const attestOffered = attestStaged && attestOfferable(prefill.read, contractVehicle);
    /** The door that cannot be opened here, and the thing to go and fix instead. */
    const attestUnrecorded = attestStaged && !attestOffered && attestable(prefill.read);
    const attestationStands = attestationHolds(
        prefill.read,
        contractVehicle,
        attestation,
        file,
        dates,
    );
    /** Typed, and not this car's. Silent on an empty box: nothing has been claimed yet. */
    const attestPlateDisagrees =
        attestPlate.trim() !== '' && !attestedPlateAgrees(attestPlate, vehicle.plate);
    /** The period is the other half of the claim, so there is nothing to confirm without it. */
    const attestReady =
        attestOffered &&
        attestedPlateAgrees(attestPlate, vehicle.plate) &&
        dates.startsAt !== '' &&
        dates.expiresAt !== '';

    /**
     * THE REFUSALS THIS ROW RAISES BEFORE ANYBODY PRESSES SAVE — all five of them since the
     * 2026-09-12 ruling: unreadable (which is how an image is answered), not a Certificate of
     * Insurance, no identifier printed, nothing on the vehicle to check it against, and the
     * mismatch this slot started with.
     *
     * It goes to `InlineUpload`'s `error` rather than to its `note`, and that is the whole
     * difference between the two slots: `error` turns the row `.sd-up-errored`, sets
     * `aria-invalid` on the Choose/Remove control and gives the refused group somewhere to land
     * the caret — which is what a refusal is FOR. The note is where a read's remarks go, and a
     * remark is not what this is.
     *
     * AND IT IS SAID ONCE. The note is suppressed for a rejected read below rather than printing
     * the same sentence a line lower: two identical sentences stacked on one row read as two
     * different problems, and the second one is the one nobody can act on.
     *
     * GUARDED ON THE SAME IDENTITY THE NOTE IS: it belongs to the file staged right now, or to
     * nothing.
     *
     * AND IT STANDS DOWN WHILE THE DOOR IS OPEN, NOT ONLY WHILE THE ATTESTATION HOLDS. This
     * sentence ends `It cannot be filed against this contract.`; the offer drawn immediately under
     * it opens `This certificate could not be read. File it by confirming what it says.` One
     * document, two instructions, one line apart — the pair teaches an admin that neither sentence
     * is worth reading. The offer opens on `UNREADABLE_REFUSAL_PREFIX`, so it carries the whole
     * statement on its own; nothing is weakened by the row going quiet, because the row was never
     * the gate — the write re-reads the bytes and refuses them itself.
     */
    const stagedRejection =
        file !== null &&
        prefill.file === file &&
        prefill.read?.status === 'rejected' &&
        // THE REFUSAL STANDS DOWN WHILE THE ATTESTATION HOLDS, WHICH IS THE WHOLE OF THE OFFER.
        // A row still wearing `.sd-up-errored` under a box the admin has just ticked is a refusal
        // the app has no intention of honouring, and the save guard agrees with this to the letter
        // — it asks `certificateAccepted`, which asks `attestationHolds`.
        !attestationStands &&
        !attestOffered
            ? certificateRefusal(prefill.read, true)
            : null;

    // GUARDED ON IDENTITY — this is the clearing rule, not a defensive check. The note belongs to
    // the file staged right now or to nothing at all.
    //
    // IT IS NOT A LIVE REGION, AND THAT IS THE CANVAS RULE RATHER THAN AN OVERSIGHT: `NarratorLine`
    // owns the page's one polite region (see `StatsLedger` and `Manifest`), and a second one here
    // would talk over the sentence that actually changed. `InlineUpload` composes this note into
    // the `aria-describedby` of the Choose/Replace and Remove buttons instead, so it is read out
    // as the description of the control the admin just operated and is reachable from it after.
    const certificateNote =
        file && prefill.file === file && prefillSentence && prefillSentence.tone !== 'rejected' ? (
            <>
                {prefillSentence.tone === 'reading' && (
                    <Loader2 aria-hidden="true" className="mr-1.5 inline h-3 w-3 animate-spin" />
                )}
                {prefillSentence.text}
                {prefillAction && !saving && (
                    <>
                        {' '}
                        <LinkLike onClick={() => actOnPrefill(prefillAction.kind)}>
                            {prefillAction.label}
                        </LinkLike>
                    </>
                )}
                {periodIsFixed && ` ${PERIOD_IS_FIXED}`}
            </>
        ) : null;

    /**
     * THE TWO SENTENCES THAT POINT AT THE DATE FIELDS POINT THE OTHER WAY FROM DOWN HERE.
     *
     * `prefillLine` writes for the staged door, whose row sits ABOVE the two date inputs, so it
     * says `Filled in below.` and `which is what the dates below say`. This note prints beneath the
     * whole group, beside `Open the certificate`, and every other word of those sentences is right
     * — so the direction is substituted rather than the sentence forked, in this one place, and
     * `prefillLine` stays the single author of the prose.
     */
    const pointingUpwards = (text: string) =>
        text
            .replace('Filled in below.', 'Filled in above.')
            .replace('dates below say', 'dates above say');

    /**
     * THE SAME SENTENCE, ONE ROW LOWER, BECAUSE THE UPLOAD'S `note` SLOT BELONGS TO THE STAGED FILE.
     * `InlineUpload` composes its note into the `aria-describedby` of the Choose and Remove buttons,
     * which is exactly right for a document being picked and wrong for one that is already filed:
     * the controls it would describe are not the ones that started this read. So an on-file read
     * prints under the hint that carries its link instead, next to `Open the certificate` — the
     * other thing an admin does with the document this is about.
     *
     * GUARDED ON IDENTITY, WHICH IS THE CLEARING RULE AND NOT A DEFENSIVE CHECK — and on the slot
     * being occupied at all, because `null === null` would otherwise draw the line over an idle
     * prefill.
     */
    const onFileSubject = onFileRef.current;
    const onFileNote =
        onFileSubject !== null && prefill.file === onFileSubject && prefillSentence ? (
            <>
                {prefillSentence.tone === 'reading' && (
                    <Loader2 aria-hidden="true" className="mr-1.5 inline h-3 w-3 animate-spin" />
                )}
                {/* The one sentence this door writes for itself: the wait covers a fetch as well as
                    a parse, and `Reading the certificate…` would be describing a document the tab
                    does not have yet. Every other sentence is `prefillLine`'s — to the word, but
                    for the two that point at the date fields and are pointed the other way. */}
                {prefillSentence.tone === 'reading'
                    ? 'Reading the certificate on file…'
                    : pointingUpwards(prefillSentence.text)}
                {prefillAction && !saving && (
                    <>
                        {' '}
                        <LinkLike onClick={() => actOnPrefill(prefillAction.kind)}>
                            {prefillAction.label}
                        </LinkLike>
                    </>
                )}
                {periodIsFixed && ` ${PERIOD_IS_FIXED}`}
            </>
        ) : null;

    /**
     * THE WARNING ABOUT THE CERTIFICATE ALREADY ON FILE, AND IT IS A WARNING AND NOT A REFUSAL.
     *
     * `certificateRefusal` with no `cannotFile` — the same author, the same family, without the
     * clause only a document being CHOSEN may be told. It stands wherever the filed certificate
     * cannot be verified: unreadable, not a Certificate of Insurance, printing no identifier, a
     * vehicle record with nothing to check it against, or naming another car.
     *
     * NOT DRAWN OVER A STAGED FILE. That row has its own read and its own refusal, and two
     * sentences about two different documents under one block is two problems where there is one.
     *
     * AND NOT DRAWN OVER AN ATTESTED FILING [D-B56], WHICH IS THE OTHER HALF OF THE OFFER. The
     * standing line below says what is true of that document — who filed it, when, and that
     * nothing here can read it — and a refusal stacked above it would be the app disowning a
     * filing it authorised.
     */
    const onFileWarning =
        file === null && onFileDecision?.status === 'rejected' && attestedOnFile === null
            ? certificateRefusal(onFileDecision)
            : null;

    /**
     * THE STANDING LINE THE ATTESTED FILING PRINTS INSTEAD [D-B56], in the same `.sd-banner` the
     * warning would have worn — the fact is advisory either way, and giving the two different
     * weights would say the tab has an opinion about which is the more serious.
     *
     * THE DATE GOES THROUGH `dayText`, so a timestamptz is read as the Ghana calendar day it
     * landed on rather than as the viewer's — the same rule every other date here obeys.
     */
    const attestedLine =
        file === null && attestedOnFile !== null && attestorName !== null
            ? attestedFilingLine(attestorName, dayText(attestedOnFile.attestedAt))
            : null;

    /**
     * THE SAME FACT, THREE WORDS LONG, FOR THE HEAD THAT IS READ WITH THE BLOCK CLOSED.
     *
     * The summary is where an admin scanning a contract stops, and a warning that only exists
     * inside an expanded block is a warning they never see. The three tails split on the two
     * distinctions worth making at that length: a document that names ANOTHER CAR is a specific,
     * actionable wrong; one filed on an admin's reading is a fact about who stands behind it; and
     * everything else is the same fact — nobody can check this one.
     */
    const certificateFlag =
        attestedLine !== null
            ? attestedSummaryTail(attestorName)
            : onFileWarning === null
              ? null
              : onFileDecision?.status === 'rejected' && onFileDecision.reason === 'mismatch'
                ? 'certificate names another vehicle'
                : 'certificate not verified';

    /*
     * THE TWO DERIVED FACTS A HOST MAY NEED OUTSIDE THIS ROW'S RENDER — a create bar's refusal list,
     * a rail row's tail. Both are returned as values too; these are for the hosts that keep them in
     * state of their own, and they report only what changed.
     *
     * THE CALLBACKS ARE HELD IN REFS AND ARE NOT DEPENDENCIES, WHICH THESE TWO ALONE NEED. Every
     * other callback this hook takes is either the host's stable dispatcher or is only ever called
     * from an event handler; the whole purpose of these two is to WRITE HOST STATE, so a host that
     * passes an inline arrow — the obvious way to write one — would get effect, setState, render, a
     * new arrow, effect, for as long as the value keeps changing. Requiring stability in a docblock
     * would put the loop one forgotten `useCallback` away; reading the latest through a ref cannot
     * be forgotten, and there is no staleness to trade for it because the effect runs on the fact
     * and calls whichever function is current when it does.
     *
     * ASSIGNED AT RENDER — the `live` idiom above, one function wide, twice.
     */
    const rejectionHost = useRef(onRejection);
    rejectionHost.current = onRejection;
    const flagHost = useRef(onFlagChange);
    flagHost.current = onFlagChange;

    useEffect(() => {
        rejectionHost.current?.(stagedRejection);
    }, [stagedRejection]);
    useEffect(() => {
        flagHost.current?.(certificateFlag);
    }, [certificateFlag]);

    const ON_FILE_NOTE_ID = `${id}-onfile`;
    const ON_FILE_WARN_ID = `${id}-onfile-warn`;
    const ATTESTED_LINE_ID = `${id}-attested`;

    const upload = (
        <InlineUpload
            id={id}
            label="Certificate"
            current={{ url: filed.url }}
            file={file}
            onFileChange={stageCertificate}
            /* THE IMAGES STAY IN `accept` AND ARE REFUSED ON THE ROW. A JPEG has no text layer, so
               since the 2026-09-12 ruling it ends `unreadable` and cannot be filed — but narrowing
               the picker would make the file the admin is looking at invisible in it, and a rule is
               easier to learn from a sentence than from a file that will not appear. Narrowing
               `accept` is a separate call. */
            accept=".pdf,.jpg,.jpeg,.png"
            disabled={shut}
            empty="Not on file · optional"
            /* THE STAGE-TIME REFUSAL FIRST, THE WRITE'S SECOND, AND EITHER MAY STAND ALONE. The save
               guard re-reads the document against the same rule, so the two never CONTRADICT each
               other — but they are asked at different moments and of different inputs: the stage
               read compares against `live.current.vehicle` when the parse resolves, the guard
               against the vehicle as it stands at Save. A certificate staged before the vehicle
               envelope landed therefore reads `unverified` here and is refused on Save, and the
               fallthrough is what prints that second answer. */
            error={stagedRejection ?? serverError}
            note={certificateNote}
        />
    );

    const attestBlock = (
        <>
            {/* AND WHERE THERE IS NOTHING ON THIS CONTRACT TO CONFIRM THE DOCUMENT AGAINST, THE
                THING TO GO AND FIX INSTEAD OF THE DOOR. A vehicle with no registration on file can
                never arm the confirmation below, so it is withheld and the admin is sent to the
                Vehicle block rather than back to an insurer whose paper is perfectly good. */}
            {attestUnrecorded && (
                <div className="sd-banner">
                    <span className="sd-banner-mark" aria-hidden="true" />
                    <p>{ATTEST_VEHICLE_UNRECORDED}</p>
                </div>
            )}
            {/*
              * ── FILING A SCAN ON THE ADMIN'S OWN READING [D-B56] ──
              *
              * A SIBLING OF THE UPLOAD ROW AND NEVER ITS `note`. That slot is composed into the
              * `aria-describedby` of the Choose and Remove buttons, which is right for a sentence
              * ABOUT the document and wrong for two controls: a text input and a checkbox read out
              * as the description of a button are not reachable from it, and a live region is not
              * this canvas's to open (`NarratorLine` owns the one polite region on the page).
              *
              * THE CANVAS IDIOM, NOT A FORM. The plate is a `QuietField` like every other scalar on
              * the block — the value at rest IS the control — and the two sentences wear the
              * `.sd-banner` the period warning wears a few rows down, which is this canvas's one
              * weight for a standing fact. A bare checkbox dropped into `.sd-rows` would be the
              * only control on the dossier that is neither a row nor a banner.
              *
              * IT IS DRAWN ONLY ON THE ONE ENDING IT ANSWERS, so the ordinary refused document is
              * refused and nothing else appears under it — and the upload row goes quiet while it
              * stands, because the refusal it would print and this offer say the same thing about
              * the same document.
              */}
            {attestOffered && (
                <>
                    <div className="sd-banner">
                        <span className="sd-banner-mark" aria-hidden="true" />
                        <p>{ATTEST_OFFER}</p>
                    </div>
                    <QuietField
                        id={`${id}Plate`}
                        label={ATTEST_PLATE_LABEL}
                        value={attestPlate}
                        onValueChange={(value) => {
                            setAttestPlate(value);
                            // ANY EDIT UN-TICKS. The claim was about the plate as it read a
                            // keystroke ago, and nothing else here can see that.
                            onAttestation(null);
                        }}
                        placeholder="—"
                        error={attestPlateDisagrees ? ATTEST_PLATE_DISAGREES : null}
                        disabled={shut}
                    />
                    {/* THE CONFIRMATION APPEARS ONLY ONCE THE PLATE AGREES, and asks for the period
                        first when the period is not there yet: the claim names both, so there is
                        nothing to tick while half of it is missing. */}
                    {attestedPlateAgrees(attestPlate, vehicle.plate) && (
                        <div className="sd-banner">
                            <span className="sd-banner-mark" aria-hidden="true" />
                            <p>
                                {attestReady ? (
                                    <label
                                        className="flex items-start gap-2"
                                        htmlFor={`${id}Attest`}
                                    >
                                        <Checkbox
                                            id={`${id}Attest`}
                                            className="mt-[3px]"
                                            checked={attestationStands}
                                            disabled={shut}
                                            onCheckedChange={(checked) =>
                                                onAttestation(
                                                    checked === true && file !== null
                                                        ? {
                                                              file,
                                                              plate: attestPlate,
                                                              startsAt: dates.startsAt,
                                                              expiresAt: dates.expiresAt,
                                                          }
                                                        : null,
                                                )
                                            }
                                        />
                                        <span>
                                            {attestCheckboxLabel(
                                                attestPlate.trim(),
                                                policyPeriodText(dates.startsAt, dates.expiresAt) ??
                                                    '',
                                            )}
                                        </span>
                                    </label>
                                ) : (
                                    ATTEST_DATES_FIRST
                                )}
                            </p>
                        </div>
                    )}
                </>
            )}
        </>
    );

    /* UNDER BOTH DATES, BECAUSE IT IS ABOUT THE SPAN AND NOT ABOUT EITHER END — neither field is
       wrong on its own. The marked sentence is the canvas's sanctioned weight for a standing fact;
       `--warning` stays the waiting dot. */
    const periodBanner = periodWarning ? (
        <div className="sd-banner" id={PERIOD_WARNING_ID}>
            <span className="sd-banner-mark" aria-hidden="true" />
            <p>{periodWarning}</p>
        </div>
    ) : null;

    const footer = (
        <>
            {filed.url && (
                <Hint className="mt-3">
                    <LinkLike onClick={() => openSignedUrl(filed.url)}>
                        Open the certificate
                    </LinkLike>
                    {/* BESIDE THE LINK THAT OPENS THE SAME DOCUMENT, AND IT IS NO LONGER WHAT STARTS
                        THE READ. The read is automatic; this spends a second signed URL on the same
                        document, which is what an admin wants after correcting the plate on the
                        Vehicle block or replacing the paper at the insurer. NOT DRAWN WHILE A FILE
                        IS STAGED — that document has its own read, and the row is about it — nor
                        while the host is committing or its read is still in flight, which are the
                        two states no read may land in. */}
                    {!file && !shut && (
                        <>
                            {' · '}
                            {/* THE READ-OUT BELONGS TO THIS LINK, so the link says so: the sentence
                                it writes is two rows down and a screen reader moving control to
                                control would never reach it otherwise. Pointed at only while there
                                is something to point at — a dangling `aria-describedby` is read as
                                nothing at all. */}
                            <LinkLike
                                onClick={() => {
                                    // THE WAY BACK LEAVES WITH THE READ THAT JUSTIFIED IT. A fresh
                                    // read of the filed document retires the standing one, and
                                    // `Put back …` goes with it — so the pair it remembered is
                                    // restored FIRST, and the new read lands over that and captures
                                    // the restored values as its own. A no-op unless the fields
                                    // still read exactly what the departing read said; an edit
                                    // since is never undone.
                                    putBackBeforeANewRead();
                                    // AND THIS IS THE ONE PLACE A FILED SCAN MAY BE OCR'd. The
                                    // automatic read that fires on every policy view may not spend
                                    // a pass; a click is an admin asking, so the next filed read —
                                    // this one — is allowed the expensive answer. Named by document
                                    // and consumed inside `readFiledCertificate`, once its bytes
                                    // are down.
                                    ocrOnNextFiledRead(onFileUrl as string);
                                    void onFile.refetch();
                                }}
                                aria-describedby={
                                    onFileWarning
                                        ? ON_FILE_WARN_ID
                                        : attestedLine
                                          ? ATTESTED_LINE_ID
                                          : onFileNote
                                            ? ON_FILE_NOTE_ID
                                            : undefined
                                }
                            >
                                Check again
                            </LinkLike>
                        </>
                    )}
                </Hint>
            )}
            {/* THE WAIT COVERS A FETCH AS WELL AS A PARSE, so it does not say `Reading` — the tab
                does not have the document yet. `isFetching` rather than `isPending`, so `Check
                again` says so too. BOTH THIS LINE AND THE ONE BELOW ARE SILENT WHILE A FILE IS
                STAGED WITHOUT A GUARD OF THEIR OWN: the staged condition is in the query KEY, so
                the entry these read is a different, disabled one. */}
            {filed.url && onFile.isFetching && (
                <Hint className="mt-1">
                    <Loader2 aria-hidden="true" className="mr-1.5 inline h-3 w-3 animate-spin" />
                    {/* AND IT SAYS SO ONCE THE READ TURNS OUT TO BE A SCAN — the same sentence the
                        staged row prints, from the same const, because it is the same fact and the
                        same seconds. */}
                    {onFileScanning ? SCAN_READING : 'Checking the certificate on file…'}
                </Hint>
            )}
            {/* THE FETCH'S OWN ENDING, AND IT IS NOT A WARNING ABOUT THE DOCUMENT. Nothing has
                looked at the paper, so a banner saying it could not be verified would be a claim
                about a certificate that is very likely fine. It is a plain line, and `Check again`
                is the way on. */}
            {filed.url && !onFile.isFetching && onFile.isError && (
                <Hint className="mt-1">{FETCH_FAILED_NOTE}</Hint>
            )}
            {onFileNote && (
                <Hint className="mt-1" id={ON_FILE_NOTE_ID}>
                    {onFileNote}
                </Hint>
            )}
            {/* THE STANDING-FACT INSTRUMENT, WHICH IS THIS CANVAS'S ONE WEIGHT FOR AN ADVISORY —
                the same `.sd-banner` the period warning wears two rows up, with no colour of its
                own. UNDER THE `Open the certificate` HINT because that is the only thing left to do
                about it: the document is filed, this cannot un-file it, and the sentence asks the
                admin to read the paper against the record. */}
            {onFileWarning && (
                <div className="sd-banner" id={ON_FILE_WARN_ID}>
                    <span className="sd-banner-mark" aria-hidden="true" />
                    <p>{onFileWarning}</p>
                </div>
            )}
            {/* AND WHERE THE DOCUMENT WAS FILED ON AN ADMIN'S READING, THE FACT RATHER THAN THE
                REFUSAL [D-B56]. Same instrument, same place, because it answers the same question
                an admin arrives at this row with — what is the standing of the paper on this policy
                — and the answer here is that a named person read it on a named day and the tab
                still cannot. */}
            {attestedLine && (
                <div className="sd-banner" id={ATTESTED_LINE_ID}>
                    <span className="sd-banner-mark" aria-hidden="true" />
                    <p>{attestedLine}</p>
                </div>
            )}
        </>
    );

    return {
        upload,
        attestBlock,
        periodBanner,
        footer,
        rejection: stagedRejection,
        /*
         * ASKED OF THE DRAFT BEING SAVED, NOT OF A RENDER-TIME FLAG. The draft is what the commit
         * is sending, so a confirmation given over one period and a Save pressed over another
         * cannot both be true — and the guard inside the write re-asks the same question of the
         * bytes it is about to upload anyway.
         */
        attestationFor: (draft: DatePair) =>
            attestationHolds(prefill.read, contractVehicle, attestation, file, draft)
                ? attestation
                : null,
        /*
         * AN OFFER STANDING UN-TICKED. A create bar asks this rather than the display refusal,
         * which has already stood down for exactly this case — so the hint names the missing tick
         * instead of quoting a server sentence about a document the admin has been told they may
         * file on their own reading.
         */
        standsUnticked: attestOffered && !attestationStands,
        flag: certificateFlag,
        dateDescribedBy: periodWarning ? PERIOD_WARNING_ID : undefined,
        reset,
    };
}
